import React, { useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { goAdmin } from '../../lib/adminLinks';
import { searchHelp } from './helpContent';

const isTyping = (el) => {
  if (!el) return false;
  if (el.isContentEditable || el.closest?.('[contenteditable="true"], [role="textbox"], .ProseMirror, .ql-editor, .monaco-editor')) return true;
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file'].includes((el.type || '').toLowerCase());
  return false;
};

const modalOpen = () => document.querySelector('[role="dialog"], [aria-modal="true"]')
  || [...document.querySelectorAll('.fixed.inset-0')].some((el) => el.getClientRects().length > 0);

const snippet = (t, q) => {
  const w = q.trim().toLowerCase().split(/\s+/)[0] || '';
  const src = [t.desc, ...(t.means || []), ...(t.todo || []), ...(t.points || [])].find((x) => x.toLowerCase().includes(w)) || t.desc;
  return src.length > 140 ? `${src.slice(0, 140)}…` : src;
};

// Press "?" anywhere in the admin (not while typing) to search Help and jump to a topic.
export const HelpQuickSearch = () => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const inputRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '?' || e.ctrlKey || e.metaKey || e.altKey || isTyping(e.target) || modalOpen()) return;
      e.preventDefault();
      setQ(''); setSel(0); setOpen(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const results = searchHelp(q).slice(0, 12);
  const pick = (t) => { setOpen(false); goAdmin('help-guide', { topic: t.id }); };
  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(s + 1, results.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
    if (e.key === 'Enter' && results[sel]) { e.preventDefault(); pick(results[sel]); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-xl p-0 gap-0 overflow-hidden" data-testid="help-quick-search" onOpenAutoFocus={(e) => { e.preventDefault(); inputRef.current?.focus(); }}>
        <DialogTitle className="sr-only">Search Help</DialogTitle>
        <DialogDescription className="sr-only">Search admin pages, Help topics and Help text</DialogDescription>
        <div className="flex items-center gap-2 border-b pl-4 pr-12">
          <Search className="w-4 h-4 text-neutral-400" />
          <input ref={inputRef} value={q} onChange={(e) => { setQ(e.target.value); setSel(0); }} onKeyDown={onKeyDown}
            placeholder="Search Help — pages, topics, how-tos…" className="flex-1 h-12 bg-transparent text-sm outline-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 shadow-none" data-testid="help-quick-search-input" />
        </div>
        <div className="max-h-96 overflow-y-auto" data-testid="help-quick-search-results">
          {!q.trim() ? <p className="px-4 py-6 text-sm text-neutral-500">Type to search, e.g. “approve sales order” or “needs pricing”.</p>
            : !results.length ? <p className="px-4 py-6 text-sm text-neutral-500" data-testid="help-quick-search-empty">No Help topics match.</p>
              : results.map((t, i) => (
                <button key={t.id} type="button" onMouseMove={() => sel !== i && setSel(i)} onClick={() => pick(t)} data-testid={`help-quick-result-${t.id}`}
                  className={`w-full text-left px-4 py-2.5 transition-colors ${i === sel ? 'bg-amber-50' : 'hover:bg-neutral-50'}`}>
                  <p className="text-sm font-medium text-neutral-900">{t.title} <span className="text-xs font-normal text-neutral-400">· {t.page || t.sectionTitle}</span></p>
                  <p className="text-xs text-neutral-500 line-clamp-1">{snippet(t, q)}</p>
                </button>
              ))}
        </div>
        <p className="border-t px-4 py-2 text-[11px] text-neutral-400">↑ ↓ to move · Enter to open · Esc to close</p>
      </DialogContent>
    </Dialog>
  );
};
